<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { Popup } from '@progress/kendo-vue-popup'
import { heartOutlineIcon, heartIcon } from '@progress/kendo-svg-icons'
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '@/logger.ts'
import { SetIsLoading } from '@/services/AoeHelpers.ts'
import {
  GetWishListItems,
  AddItemToWishList,
  RemoveItemFromWishLists,
} from '@/services/WishListService'
//import { CartLocalStorageKey } from '@/services/CartService.ts'
import type {
  IAoeAllWishList,
  IAoeAllWishListSkus,
} from '@/types/WishListTypes'
import { ref, onMounted, onBeforeUnmount } from 'vue'
import {
  GetBeeNumberAuthenicatedUser,
  GetLoginUrl,
} from '@/stores/SettingsStore'
import type { ComponentPublicInstance } from 'vue'

const logger = useLogger()
setStoreLogger(logger)

logger.debug('AOEWishlistImageButton.vue')

const showPopup = ref(false)
const skus = ref([] as IAoeAllWishListSkus[])
const skuInWishlist = ref(false)
const inProductDetail = ref(false)
const buttonClass = ref('btnwishListHeart')
const rootClass = ref('wishlistBtn')
const popupElement = ref<HTMLElement | null>(null)
const btnUnfilledHeart = ref<ComponentPublicInstance | null>(null) // matches ref from template

const anchorAlign = {
  horizontal: 'center',
  vertical: 'top',
}

const popupAlign = {
  horizontal: 'left',
  vertical: 'bottom',
}

const props = defineProps<{
  skucode: string
  isproductdetail?: boolean
  insearchresult?: boolean
}>()

logger.debug(
  'GetWishListItems Define Props skucode:',
  props.skucode,
  'isproductdetail:',
  props.isproductdetail,
)

if (props.isproductdetail) {
  buttonClass.value = 'btnProdDetailWishListHeart'
  inProductDetail.value = true
}
else {
  rootClass.value = rootClass.value + ' category-detail-wishlist-button'
  if (props.insearchresult) {
    rootClass.value = rootClass.value + ' search-result'
  }
}

interface IViewModel {
  wishListName: string
  aoeWishlistId: number
  skuCount: number
}
const viewModels = ref<Array<IViewModel>>([])

onMounted(async () => {
  const data = await GetWishListItems()
  skus.value = data.skus
  UpdateViewModels(data)
  for (let i = 0; i < skus.value.length; i++) {
    if (skus.value[i].skuCode === props.skucode) {
      logger.debug('GetWishListItems sku in list', props.skucode)
      skuInWishlist.value = true
      break
    }
  }
  window.addEventListener('click', handleClickOutside)
  window.addEventListener('storage', storageHandler, false)
})

onBeforeUnmount(() => {
  window.removeEventListener('click', handleClickOutside)
})

function UpdateViewModels(data: IAoeAllWishList) {
  viewModels.value = []
  for (const wishlist of data.wishListNames) {
    const viewModel: IViewModel = {
      wishListName: wishlist.wishListName,
      aoeWishlistId: wishlist.aoeWishlistId,
      skuCount: 0,
    }
    for (const sku of data.skus) {
      if (sku.aoeWishlistId === wishlist.aoeWishlistId) {
        viewModel.skuCount++
      }
    }
    viewModels.value.push(viewModel)
  }
}

function storageHandler(event: StorageEvent) {
  logger.debug('storageHandler', event)
  if (event.key === 'AoeAllWishList') {
    skuInWishlist.value = false
    GetWishListItems().then(data => {
      skus.value = data.skus
      UpdateViewModels(data)
      for (let i = 0; i < skus.value.length; i++) {
        if (skus.value[i].skuCode === props.skucode) {
          skuInWishlist.value = true
          break
        }
      }
    })

    logger.debug('skus', skus.value)
  }
}

function unfilledHeartClicked() {
  logger.debug('unfilledHeartClicked', props)

  if (viewModels.value.length > 1) {
    showPopup.value = !showPopup.value
  } else {
    addToWishListClickedHandler(null)
  }
}

async function addToWishListClickedHandler(wishListName: string | null) {
  logger.debug('addWishListClickedHandler', wishListName)
  SetIsLoading(true)
  const beeNumber = await GetBeeNumberAuthenicatedUser()
  if (beeNumber) {
    showPopup.value = false
    skuInWishlist.value = true
    AddItemToWishList(wishListName, props.skucode).then(async data => {
      logger.debug('AddItemToWishList', data)
      SetIsLoading(false)
    })
  } else {
    logger.debug('addWishListClickedHandler', 'beeNumber not found')
    const returnUrl = window.location.pathname
    SetIsLoading(false)
    window.location.href =
      (await GetLoginUrl()) + '?returnUrl=' + encodeURIComponent(returnUrl)
  }
}

function removeItemFromWishListClickedHandler() {
  logger.debug('removeItemFromWishList', props)
  skuInWishlist.value = false
  SetIsLoading(true)
  RemoveItemFromWishLists(props.skucode).then(async data => {
    logger.debug('RemoveItemFromWishLists', data)
    SetIsLoading(false)
  })
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as Node

  const popup = popupElement.value
  const button = btnUnfilledHeart.value?.$el ?? null

  if (
    showPopup.value &&
    popup &&
    button &&
    !popup.contains(target) &&
    !button.contains(target)
  ) {
    showPopup.value = false
  }
}
</script>

<style lang="scss">
.filledHeart .k-svg-icon > svg {
  fill: var(--standardColor) !important;
}

.wishlist-name-popup {
  border-radius: 10%
}

.wishlist-name-popup-div {
    padding: $card-padding;
}

.wishlist-name-list-div {
    cursor: pointer;
}

.wishlist-name-list {
    padding: $spacing-3 $spacing-2;
    border-radius: $radius-sm;
    transition: background-color 0.15s ease-in-out;
}

.wishlist-name-list-div:hover .wishlist-name-list,
.wishlist-name-list-div:focus-visible .wishlist-name-list {
    background-color: var(--color-background-mute);
}

.wishlist-name-list-div:focus-visible {
    outline: none;
}

/* Mobile: ignore the anchor position and centre the popup in the viewport.
   !important is needed to override the inline top/left Kendo Popup sets. */
@media (max-width: 767.98px) {
  .k-animation-container.wishlist-popup-container {
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%);
    width: calc(100vw - (2 * #{$spacing-4}));
    max-width: 95vw;
    z-index: $z-modal;

    .wishlist-name-popup {
      width: 100%;
    }
  }
}

.anchor {
  display: block;
  width: 30px;
}

.wishlistBtn.category-detail-wishlist-button {
  right: 1px;
  background-color: var(--color-background);
  border: 0;
  width: 35px;
  height: 35px;

  position: absolute;
  top: 8px;
  right: 8px;
  border-radius: $radius-circle;
  font-size: 44px;
  z-index: 150;

  @media (max-width: 769px) {
    top: 0;
    right: 0;

    &.search-result {
      top: 1px;
      right: $spacing-3;
    }
  }

  &:hover {
    transform: translateY(-2px);
  }

  .wishListHeart {
    width: 35px;
    height: 35px;
  }

  .btnwishListHeart {
    border-radius: $radius-circle;
    position: relative;
    top: auto;
    padding: 0;

    .k-icon {
      height: 35px;
      width: 35px;

      svg {
        transform: scale(1);
        padding: 2.5px;
      }
    }
  }
}

.k-button.btnProdDetailWishListHeart,
.k-button.k-button-md.btnProdDetailWishListHeart,
.k-button.k-button-solid.btnProdDetailWishListHeart {
  display: flex !important;
  @include typography-body;
  @include font-weight-regular;
  padding: 10px 20px !important;
  margin: 0 !important;
  text-align: center;
  cursor: pointer;
  white-space: nowrap;
  text-transform: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -o-user-select: none;
  user-select: none;
  text-shadow: inherit !important;
  border-radius: $radius-md !important;
  width: 100%;
  background: none repeat scroll 0 0 white !important;
  color: black !important;
  border: 1px solid black !important;
  align-items: center !important;
  justify-content: center !important;
  gap: $spacing-2;
  min-height: 46px !important;
  box-sizing: border-box !important;

  svg {
    transform: scale(2.5);
  }

  .k-button-icon {
    margin-right: $spacing-2 !important;
  }

  // Medium screens: allow text wrapping for 2-row behavior
  @media (min-width: 768px) and (max-width: 1199px) {
    white-space: normal;
    line-height: 1.2;
    padding: $button-padding-y $button-padding-x;
    text-align: left; // Align text left when wrapped to protect icon

    .btnWishListText {
      flex: 1;
      text-align: center;
    }
  }

  // Large screens: prevent wrapping for 1-row behavior
  @media (min-width: 1200px) {
    white-space: nowrap;
    text-align: center;

    .btnWishListText {
      text-align: center;
    }
  }
}

.wishlistBtn {
  width: 100%;
  margin: 0 !important;
  padding: 0 !important;
  display: flex !important;
  align-items: stretch !important;
  vertical-align: top !important;
}

.wishlistBtn .k-button-text {
  display: inline-table;
}

.wish-list-close-button-div {
    position: absolute;
    top: 0px;
    right: 0px;
}

/* !important required: Prevent heart icons from becoming huge when printing */
@media print {
  .wishlistBtn .k-svg-icon,
  .wishlistBtn svg,
  .btnwishListHeart .k-svg-icon,
  .btnwishListHeart svg {
    max-width: 35px !important;
    max-height: 35px !important;
    width: 35px !important;
    height: 35px !important;
    transform: scale(1) !important;
  }

  .btnProdDetailWishListHeart svg {
    max-width: 20px !important;
    max-height: 20px !important;
    width: 20px !important;
    height: 20px !important;
    transform: scale(1) !important;
  }
}
</style>

<template>
  <div v-bind:id="'divUnfilledHeart_' + props.skucode" :class="rootClass" data-testid="wishlist-button-container">
    <KButton
      ref="btnUnfilledHeart"
      v-bind:id="'btnUnfilledHeart_' + props.skucode"
      v-if="!skuInWishlist"
      class="wishListHeart anchor"
      :class="buttonClass"
      :svg-icon="heartOutlineIcon"
      @click="unfilledHeartClicked"
      data-testid="add-to-wishlist-btn"
    >
      <div v-if="props.isproductdetail" class="btnWishListText">
        {{ t('wishList.WishListHeader') }}
      </div>
      <span v-if="!inProductDetail" class="sr-only">{{ t('wishList.WishListHeader') }}</span>
    </KButton>
    <Popup
      v-bind:anchor="'divUnfilledHeart_' + props.skucode"
      :show="showPopup"
      :popup-class="'wishlist-name-popup'"
      :anchor-align="anchorAlign"
      :popup-align="popupAlign"
      class="wishlist-popup-container"
      data-testid="wishlist-popup"
    >
      <div
        ref="popupElement"
        class="wishlist-name-popup-div"
        tabindex="10"
        data-testid="wishlist-popup-content"
      >
        <div class="row">
          <div class="col-12" data-testid="wishlist-save-to-label">{{ t('wishList.WishListSaveTo') }}:</div>
        </div>
        <div class="wish-list-close-button-div">
            <KButton
              :fill-mode="'clear'"
              @click="showPopup = false"
              class="popup-close-button-wishlist"
              data-testid="wishlist-popup-close-btn"
              >&#x2715;</KButton>
        </div>
        <hr />
        <div
          class="wishlist-name-list-div"
          tabindex="0"
          role="button"
          @keydown.enter.prevent="addToWishListClickedHandler(viewModel.wishListName)"
          @keydown.space.prevent="addToWishListClickedHandler(viewModel.wishListName)"
          v-for="viewModel in viewModels"
          :key="viewModel.aoeWishlistId"
          @click="addToWishListClickedHandler(viewModel.wishListName)"
          :data-testid="`wishlist-option-${viewModel.aoeWishlistId}`"
        >
          <div class="wishlist-name-list col-12 text-center" :data-testid="`wishlist-name-${viewModel.aoeWishlistId}`">
            {{ viewModel.wishListName }} - {{ viewModel.skuCount }} {{ t('wishList.WishListItems') }}
          </div>
          <hr />
        </div>
      </div>
    </Popup>
    <KButton
      v-if="skuInWishlist"
      class="filledHeart wishListHeart anchor"
      :class="buttonClass"
      @click="removeItemFromWishListClickedHandler()"
      :svg-icon="heartIcon"
      data-testid="remove-from-wishlist-btn"
    >
      <div v-if="props.isproductdetail" class="btnWishListText">
        {{ t('wishList.WishListButton') }}
      </div>
      <span v-if="!inProductDetail" class="sr-only">{{ t('wishList.WishListButton') }}</span>
    </KButton>
  </div>
</template>
